/** preset JSON 当前版本号 */
export const PRESET_VERSION = 2;

interface VersionedPresetPayload {
  v: number;
  data: unknown;
}

export function stringifyVersionedPreset(spec: unknown): string {
  const payload: VersionedPresetPayload = {
    v: PRESET_VERSION,
    data: spec,
  };
  return JSON.stringify(payload);
}

/** 将 preset 输入规范化为版本化 JSON 字符串（兼容 legacy 无 v/data 的结构） */
export function coerceVersionedPresetRaw(input: Record<string, unknown> | string): string {
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return '';

    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      throw new Error('Invalid preset JSON');
    }

    if (parsed == null || typeof parsed !== 'object') {
      throw new Error('Preset must be a JSON object');
    }

    return coerceVersionedPresetRaw(parsed as Record<string, unknown>);
  }

  if ('v' in input && 'data' in input) {
    return JSON.stringify(input);
  }

  return stringifyVersionedPreset(input);
}

export function parseVersionedPreset<T>(raw: string, migrations: Record<number, (data: unknown) => unknown> = {}): T {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Invalid preset JSON');
  }

  if (parsed == null || typeof parsed !== 'object') {
    throw new Error('Preset must be a JSON object');
  }

  const record = parsed as Record<string, unknown>;
  if (!('v' in record) || !('data' in record)) {
    throw new Error('Preset must contain version field "v" and "data"');
  }

  let version = Number(record.v);
  let data = record.data;

  if (!Number.isFinite(version)) {
    throw new Error('Preset version "v" must be a number');
  }

  const sortedVersions = Object.keys(migrations)
    .map(Number)
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  for (const targetVersion of sortedVersions) {
    if (version >= targetVersion) {
      continue;
    }
    const migrate = migrations[targetVersion];
    if (!migrate) {
      continue;
    }
    data = migrate(data);
    version = targetVersion;
  }

  if (version !== PRESET_VERSION) {
    throw new Error(`Unsupported preset version ${version}, expected ${PRESET_VERSION}`);
  }

  return data as T;
}
